import { type QueryKey } from '@tanstack/react-query';
import { type ReactElement, useCallback, useMemo, useState } from 'react';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { CoverageMetricTableCell } from '@/features/codeCoverage/pages/CoverageMetricTableCell';
import {
  resolveCoverageMetricThresholdColor,
  type CoverageMetricThresholdKey,
} from '@/features/codeCoverage/pages/coverageMetricThresholdColors';
import { resolveCoverageRunFailurePresentation } from '@/features/codeCoverage/pages/coverageRunFailurePresentation';
import { useCoverageRunTableRowActions } from '@/features/codeCoverage/pages/useCoverageRunTableRowActions';
import { useProjectMetricThresholds } from '@/features/projects/hooks/useProjectMetricThresholds';
import { DetailEditEntityDialog } from '@/features/shared/components/entityDetails/DetailEditEntityDialog';
import { TableActionsCell } from '@/features/shared/components/tableSystem/TableCells';
import {
  TableV2ActionsHeaderIcon,
  TableV2DateTimeCell,
  TableV2HeaderLabel,
  TableV2NameCell,
  TableV2TagGroup,
} from '@/features/shared/components/tableSystem/TableV2Primitives';
import { TableV2QueryShell } from '@/features/shared/components/tableSystem/TableV2QueryShell';
import { buildTableV2BodyRowSurfaceSx } from '@/features/shared/components/tableSystem/TableV2RowSurface';
import { type TableV2SkeletonColumnKind } from '@/features/shared/components/tableSystem/TableV2Skeleton';
import { buildTagColorByName, resolveEntityTags } from '@/features/shared/tags';
import { getEntityActionLabel } from '@/shared/copy/actions';
import { getDetailLabel } from '@/shared/copy/details';
import { getDialogMessage } from '@/shared/copy/dialogs';
import { getEmptyStateMessage } from '@/shared/copy/emptyStates';
import { useTagOptions } from '@/shared/hooks/useTagOptions';
import { s } from '@/theme/tokens';
import { Box, Chip, TableCell, TableRow, Text } from '@/ui/primitives';
import type { TagColor } from '@/shared/data/tags';
import type { GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type { GridColumnVisibilityModel, GridSortModel } from '@/ui/primitives';
import type {
  CoverageRunDto,
  MetricThreshold,
  QueryResult,
  QueryResult as GridQueryResult,
} from '@flowqa/shared';

type CoverageRunsTableProps = {
  runs: CoverageRunDto[];
  queryKey: QueryKey;
  projectId?: string | null;
  initialData?: GridQueryResult<CoverageRunDto>;
  loading?: boolean;
  initialSortModel?: GridSortModel;
  columnVisibilityModel?: GridColumnVisibilityModel;
  onColumnVisibilityModelChange?: (nextModel: GridColumnVisibilityModel) => void;
};

type CoverageRunColumnKey =
  | 'run'
  | 'failureTaxonomy'
  | 'lineCoverage'
  | 'functionCoverage'
  | 'branchCoverage'
  | 'statementCoverage'
  | 'details'
  | 'actions';

const coverageRunColumns: CoverageRunColumnKey[] = [
  'run',
  'failureTaxonomy',
  'lineCoverage',
  'functionCoverage',
  'branchCoverage',
  'statementCoverage',
  'details',
  'actions',
];

const columnWidths: Record<CoverageRunColumnKey, string> = {
  run: '20%',
  failureTaxonomy: '14%',
  lineCoverage: '10%',
  functionCoverage: '10%',
  branchCoverage: '10%',
  statementCoverage: '10%',
  details: '18%',
  actions: '8%',
};

const columnLabels: Record<CoverageRunColumnKey, string> = {
  run: getDetailLabel('run'),
  failureTaxonomy: 'Failure',
  lineCoverage: 'Line',
  functionCoverage: 'Function',
  branchCoverage: 'Branch',
  statementCoverage: 'Statement',
  details: getDetailLabel('details'),
  actions: getDetailLabel('actions'),
};

const skeletonColumnByKey: Record<CoverageRunColumnKey, TableV2SkeletonColumnKind> = {
  run: 'name',
  failureTaxonomy: 'status',
  lineCoverage: 'metric',
  functionCoverage: 'metric',
  branchCoverage: 'metric',
  statementCoverage: 'metric',
  details: 'name',
  actions: 'actions',
};

const formatPercent = (value: number | null | undefined): string =>
  value === null || value === undefined ? '-' : `${value.toFixed(2)}%`;

const formatCountPair = (
  covered: number | null | undefined,
  total: number | null | undefined,
): string => {
  if (covered === null || covered === undefined || total === null || total === undefined) {
    return '-';
  }
  return `${String(covered)} / ${String(total)}`;
};

const compareRunStrings = (
  leftValue: string | null | undefined,
  rightValue: string | null | undefined,
): number => (leftValue ?? '').localeCompare(rightValue ?? '', undefined, { sensitivity: 'base' });

const compareRunNumbers = (
  leftValue: number | null | undefined,
  rightValue: number | null | undefined,
): number => (leftValue ?? Number.NEGATIVE_INFINITY) - (rightValue ?? Number.NEGATIVE_INFINITY);

const compareRunDates = (
  leftValue: Date | string | null | undefined,
  rightValue: Date | string | null | undefined,
): number => new Date(leftValue ?? 0).getTime() - new Date(rightValue ?? 0).getTime();

const resolveCoverageRunSorts = (initialSortModel?: GridSortModel): GridQueryInput['sorts'] => {
  const firstSort = initialSortModel?.[0];
  return [
    {
      field: firstSort?.field ?? 'createdAt',
      direction: firstSort?.sort === 'asc' ? 'asc' : 'desc',
    },
  ];
};

const sortRuns = (runs: CoverageRunDto[], input: GridQueryInput): CoverageRunDto[] => {
  const sort = input.sorts[0] ?? { field: 'createdAt', direction: 'desc' as const };
  const directionFactor = sort.direction === 'asc' ? 1 : -1;

  return [...runs].sort((left, right) => {
    let result = 0;

    switch (sort.field) {
      case 'projectName':
        result = compareRunStrings(left.projectName, right.projectName);
        break;
      case 'linesPct':
        result = compareRunNumbers(left.linesPct, right.linesPct);
        break;
      case 'functionsPct':
        result = compareRunNumbers(left.functionsPct, right.functionsPct);
        break;
      case 'branchesPct':
        result = compareRunNumbers(left.branchesPct, right.branchesPct);
        break;
      case 'statementsPct':
        result = compareRunNumbers(left.statementsPct, right.statementsPct);
        break;
      case 'createdAt':
      default:
        result = compareRunDates(
          left.capturedAt ?? left.createdAt,
          right.capturedAt ?? right.createdAt,
        );
        break;
    }

    if (result !== 0) return result * directionFactor;
    return compareRunDates(left.createdAt, right.createdAt) * -1;
  });
};

const isColumnVisible = (
  columnKey: CoverageRunColumnKey,
  columnVisibilityModel?: GridColumnVisibilityModel,
): boolean => columnVisibilityModel?.[columnKey] !== false;

const getVisibleColumns = (
  columnVisibilityModel?: GridColumnVisibilityModel,
): CoverageRunColumnKey[] =>
  coverageRunColumns.filter((columnKey) => isColumnVisible(columnKey, columnVisibilityModel));

const buildColgroup = (visibleColumns: CoverageRunColumnKey[]): ReactElement => (
  <colgroup>
    {visibleColumns.map((columnKey) => (
      <col key={columnKey} width={columnWidths[columnKey]} />
    ))}
  </colgroup>
);

const buildHeader = (visibleColumns: CoverageRunColumnKey[]): ReactElement => (
  <TableRow>
    {visibleColumns.map((columnKey) => (
      <TableCell key={columnKey} align={columnKey === 'actions' ? 'center' : 'left'}>
        {columnKey === 'actions' ? (
          <TableV2ActionsHeaderIcon />
        ) : (
          <TableV2HeaderLabel label={columnLabels[columnKey]} />
        )}
      </TableCell>
    ))}
  </TableRow>
);

const MetricCell = ({
  covered,
  percent,
  threshold,
  total,
}: {
  covered: number | null | undefined;
  percent: number | null | undefined;
  threshold: MetricThreshold;
  total: number | null | undefined;
}): ReactElement => (
  <CoverageMetricTableCell
    primary={formatPercent(percent)}
    secondary={formatCountPair(covered, total)}
    primaryColor={resolveCoverageMetricThresholdColor(percent, threshold)}
  />
);

const CoverageRunFailureTaxonomyCell = ({ row }: { row: CoverageRunDto }): ReactElement => {
  const presentation = resolveCoverageRunFailurePresentation(row);
  if (!presentation) {
    return <Text role="bodyMuted">-</Text>;
  }

  return (
    <Box sx={{ display: 'grid', gap: s('xxs'), justifyItems: 'flex-start', minWidth: 0 }}>
      <Chip
        label={presentation.label}
        tone="error"
        transform="none"
        size="small"
        sx={{ maxWidth: '100%' }}
      />
      {presentation.subkindLabel ? (
        <Text role="caption" color="textSecondary">
          {presentation.subkindLabel}
        </Text>
      ) : null}
    </Box>
  );
};

const CoverageRunRow = ({
  columnVisibilityModel,
  isLastVisibleRow,
  onDeleteRow,
  onEditRow,
  onUpdateRowTagsColor,
  resolveThreshold,
  row,
  tagColorByName,
  updateRowTagsColorPendingId,
}: {
  columnVisibilityModel?: GridColumnVisibilityModel;
  isLastVisibleRow: boolean;
  onDeleteRow: (row: CoverageRunDto) => void;
  onEditRow: (row: CoverageRunDto) => void;
  onUpdateRowTagsColor: (input: {
    row: CoverageRunDto;
    tags: string[];
    colors: TagColor[];
  }) => void;
  resolveThreshold: (metricKey: CoverageMetricThresholdKey) => MetricThreshold;
  row: CoverageRunDto;
  tagColorByName?: Partial<Record<string, TagColor>>;
  updateRowTagsColorPendingId: string | null;
}): ReactElement => {
  const primaryLabel =
    row.commitSha?.slice(0, 8) ?? row.buildId ?? row.pipelineExecutionId ?? row.id.slice(0, 8);
  const secondaryParts = [
    row.projectName?.trim() ?? 'Coverage Run',
    row.branch?.trim() ?? null,
  ].filter((value): value is string => value !== null && value.length > 0);
  const visibleColumns = getVisibleColumns(columnVisibilityModel);

  return (
    <TableRow sx={buildTableV2BodyRowSurfaceSx({ hasRenderedDivider: !isLastVisibleRow })}>
      {visibleColumns.includes('run') ? (
        <TableCell>
          <Box sx={{ display: 'grid', gap: s('xs'), minWidth: 0 }}>
            <TableV2NameCell
              href={`/coverage-runs/${row.id}`}
              primary={primaryLabel}
              secondary={secondaryParts.join(' · ')}
            />
            <TableV2TagGroup tagColorByName={tagColorByName} tags={resolveEntityTags(row)} />
          </Box>
        </TableCell>
      ) : null}
      {visibleColumns.includes('failureTaxonomy') ? (
        <TableCell>
          <CoverageRunFailureTaxonomyCell row={row} />
        </TableCell>
      ) : null}
      {visibleColumns.includes('lineCoverage') ? (
        <TableCell>
          <MetricCell
            covered={row.linesCovered}
            percent={row.linesPct}
            threshold={resolveThreshold('line')}
            total={row.linesTotal}
          />
        </TableCell>
      ) : null}
      {visibleColumns.includes('functionCoverage') ? (
        <TableCell>
          <MetricCell
            covered={row.functionsCovered}
            percent={row.functionsPct}
            threshold={resolveThreshold('function')}
            total={row.functionsTotal}
          />
        </TableCell>
      ) : null}
      {visibleColumns.includes('branchCoverage') ? (
        <TableCell>
          <MetricCell
            covered={row.branchesCovered}
            percent={row.branchesPct}
            threshold={resolveThreshold('branch')}
            total={row.branchesTotal}
          />
        </TableCell>
      ) : null}
      {visibleColumns.includes('statementCoverage') ? (
        <TableCell>
          <MetricCell
            covered={row.statementsCovered}
            percent={row.statementsPct}
            threshold={resolveThreshold('statement')}
            total={row.statementsTotal}
          />
        </TableCell>
      ) : null}
      {visibleColumns.includes('details') ? (
        <TableCell>
          <Box sx={{ display: 'grid', gap: s('xs'), minWidth: 0 }}>
            <TableV2NameCell
              primary={row.uploadFileName ?? `Upload ${row.uploadId.slice(0, 8)}`}
              secondary={row.createdByEmail ?? row.createdByName ?? '-'}
            />
            <TableV2DateTimeCell value={row.capturedAt ?? row.createdAt} />
          </Box>
        </TableCell>
      ) : null}
      {visibleColumns.includes('actions') ? (
        <TableCell align="center">
          <TableActionsCell
            rowName={primaryLabel}
            verticalAlign="center"
            editActionLabel={getEntityActionLabel('edit', 'Coverage Run')}
            editTagsEntityNoun="coverage run"
            rowTags={row.tags}
            onEdit={() => {
              onEditRow(row);
            }}
            onUpdateTagsColor={(input) => {
              onUpdateRowTagsColor({ row, ...input });
            }}
            updateTagsColorLoading={updateRowTagsColorPendingId === row.id}
            deleteActionLabel={getEntityActionLabel('delete', 'Coverage Run')}
            onDelete={() => {
              onDeleteRow(row);
            }}
          />
        </TableCell>
      ) : null}
    </TableRow>
  );
};

export const CoverageRunsTable = ({
  runs,
  queryKey,
  projectId = null,
  loading = false,
  initialSortModel,
  columnVisibilityModel,
}: CoverageRunsTableProps): ReactElement => {
  const { thresholds } = useProjectMetricThresholds(projectId);
  const { tagOptions } = useTagOptions({ projectId, enabled: runs.length > 0 });
  const [editTarget, setEditTarget] = useState<CoverageRunDto | null>(null);
  const tagColorByName = useMemo(() => buildTagColorByName(tagOptions), [tagOptions]);
  const visibleColumns = useMemo(
    () => getVisibleColumns(columnVisibilityModel),
    [columnVisibilityModel],
  );
  const sorts = useMemo(() => resolveCoverageRunSorts(initialSortModel), [initialSortModel]);
  const resolveThreshold = useCallback(
    (metricKey: CoverageMetricThresholdKey) => thresholds.coverageRuns[metricKey],
    [thresholds.coverageRuns],
  );
  const {
    deleteLoading,
    deleteTarget,
    onDeleteClose,
    onDeleteConfirm,
    onDeleteRow,
    onUpdateRowTagsColor,
    updateRowTagsColorPendingId,
  } = useCoverageRunTableRowActions();
  const queryFn = useCallback(
    async (input: GridQueryInput): Promise<QueryResult<CoverageRunDto>> => {
      const sortedRuns = sortRuns(runs, input);
      const page = Math.max(input.page, 1);
      const pageSize = input.pageSize;
      const startIndex = (page - 1) * pageSize;
      return await Promise.resolve({
        items: sortedRuns.slice(startIndex, startIndex + pageSize),
        total: sortedRuns.length,
        page,
        pageSize,
      });
    },
    [runs],
  );

  return (
    <>
      <TableV2QueryShell
        active
        ariaLabel="Coverage Runs"
        colgroup={buildColgroup(visibleColumns)}
        emptyText={getEmptyStateMessage('entity.noneFound', { entity: 'coverageRun' })}
        entityLabel="coverage runs"
        forceLoading={loading}
        header={buildHeader(visibleColumns)}
        pageSizeOptions={[10, 25, 50]}
        queryFn={queryFn}
        queryKey={queryKey}
        skeletonColumns={visibleColumns.map((columnKey) => skeletonColumnByKey[columnKey])}
        sorts={sorts}
        renderRow={(row, context) => (
          <CoverageRunRow
            key={row.id}
            columnVisibilityModel={columnVisibilityModel}
            isLastVisibleRow={context.isLastVisibleRow}
            resolveThreshold={resolveThreshold}
            row={row}
            tagColorByName={tagColorByName}
            updateRowTagsColorPendingId={updateRowTagsColorPendingId}
            onDeleteRow={onDeleteRow}
            onEditRow={setEditTarget}
            onUpdateRowTagsColor={onUpdateRowTagsColor}
          />
        )}
      />
      <ConfirmDialog
        open={deleteTarget !== null}
        title={getDialogMessage('destructive.delete.title', { entity: 'coverageRun' })}
        description="This coverage run and its associated directory and file coverage will be deleted."
        confirmLabel={getDialogMessage('destructive.delete.confirmLabel', {
          entity: 'coverageRun',
        })}
        confirmColor="error"
        loading={deleteLoading}
        onClose={onDeleteClose}
        onConfirm={onDeleteConfirm}
      />
      <DetailEditEntityDialog
        open={editTarget !== null}
        entityLabel="Coverage Run"
        entityName={editTarget?.commitSha?.slice(0, 8) ?? editTarget?.id.slice(0, 8) ?? ''}
        projectId={editTarget?.projectId ?? null}
        tags={editTarget?.tags ?? []}
        loading={updateRowTagsColorPendingId === (editTarget?.id ?? null)}
        cascadeCopy="Tags will be applied to this coverage run and reflected across associated coverage views."
        onClose={() => {
          if (updateRowTagsColorPendingId === (editTarget?.id ?? null)) return;
          setEditTarget(null);
        }}
        onSave={({ tags, colors }) => {
          if (!editTarget) return;
          onUpdateRowTagsColor({ row: editTarget, tags, colors });
          setEditTarget(null);
        }}
      />
    </>
  );
};
